import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  INSPECTION_SCALES,
  INSPECTION_OVERVIEW_ANGLES,
  inspectionTemplateItemScale,
  inspectionScaleOption,
  carLabel,
  type InspectionItemState,
  type InspectionOverviewAngle,
  type InspectionScaleKey,
  type InspectionUpdate,
} from '@garage/shared'
import {
  PageHeader,
  Button,
  TextField,
  Segmented,
  Skeleton,
  StatusDot,
  EmptyState,
  useSnackbar,
  PrintIcon,
  AddIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'

type FormItems = Record<string, InspectionItemState>
type FormSectionNotes = Record<string, string>
type FormOverviewPhotos = Record<string, string | null>

const EMPTY_ITEM: InspectionItemState = { status: '', comment: null, photoPath: null }

/**
 * The vehicle inspection checklist for one jobcard. Sections and items come
 * from the editable template (Settings → Inspection Checklist); only the
 * per-item status, comments and photos, plus a few free-text notes, are
 * specific to this car. Status/comment/notes are saved as one PUT that
 * merges into whatever is already stored; photos are their own upload
 * endpoint per item (and one more set of four for a whole-vehicle overview),
 * so a photo survives an unrelated save.
 */
export function InspectionReportPage() {
  const { id } = useParams<{ id: string }>()
  const jobcardId = id!
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const { t } = useTranslation()

  const scaleOptionLabel = (scaleKey: InspectionScaleKey, value: string, fallback: string) =>
    t(`inspection.scales.${scaleKey}.${value}`, fallback)
  const overviewAngleLabel = (angle: InspectionOverviewAngle, fallback: string) =>
    t(`inspection.overviewAngles.${angle}`, fallback)

  const { data: jobcard } = useQuery({
    queryKey: qk.jobcard(jobcardId),
    queryFn: () => api.jobcards.get(jobcardId),
  })
  const { data: inspection, isLoading } = useQuery({
    queryKey: qk.inspection(jobcardId),
    queryFn: () => api.inspections.get(jobcardId),
  })
  const { data: template } = useQuery({
    queryKey: qk.inspectionTemplate,
    queryFn: api.inspectionTemplate.tree,
  })
  const sections = template?.items ?? []

  const [items, setItems] = useState<FormItems>({})
  const [sectionNotes, setSectionNotes] = useState<FormSectionNotes>({})
  const [overviewPhotos, setOverviewPhotos] = useState<FormOverviewPhotos>({})
  const [performedAt, setPerformedAt] = useState('')
  const [performedBy, setPerformedBy] = useState('')
  const [overallNotes, setOverallNotes] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [loaded, setLoaded] = useState(false)
  // Track photo versions for cache-busting after retakes. Keyed by itemId or 'overview-<angle>'
  const [photoVersions, setPhotoVersions] = useState<Record<string, number>>({})

  // Loads once, from whatever was already saved — a save afterwards updates
  // the cache directly rather than round-tripping through this effect again.
  useEffect(() => {
    if (inspection && !loaded) {
      setItems(inspection.items)
      setSectionNotes(
        Object.fromEntries(
          Object.entries(inspection.sectionNotes).filter((entry): entry is [string, string] => entry[1] != null),
        ),
      )
      setOverviewPhotos(inspection.overviewPhotos)
      setPerformedAt(inspection.performedAt ?? '')
      setPerformedBy(inspection.performedBy ?? '')
      setOverallNotes(inspection.overallNotes ?? '')
      setLoaded(true)
    }
  }, [inspection, loaded])

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload: InspectionUpdate = {
        items: Object.fromEntries(
          Object.entries(items).map(([key, value]) => [key, { status: value.status, comment: value.comment }]),
        ),
        sectionNotes,
        performedAt: performedAt.trim() || null,
        performedBy: performedBy.trim() || null,
        overallNotes: overallNotes.trim() || null,
      }
      return api.inspections.save(jobcardId, payload)
    },
    onSuccess: (data) => {
      queryClient.setQueryData(qk.inspection(jobcardId), data)
      snackbar.success(t('inspection.inspectionSaved'))
    },
    onError: (error) => snackbar.error(error instanceof ApiError ? error.message : t('inspection.failedToSave')),
  })

  const getItem = (itemId: string): InspectionItemState => items[itemId] ?? EMPTY_ITEM

  const setItemStatus = (itemId: string, status: string) => {
    setItems((prev) => ({
      ...prev,
      [itemId]: { status, comment: prev[itemId]?.comment ?? null, photoPath: prev[itemId]?.photoPath ?? null },
    }))
  }

  const setItemComment = (itemId: string, comment: string) => {
    setItems((prev) => ({
      ...prev,
      [itemId]: {
        status: prev[itemId]?.status ?? '',
        comment: comment.trim() === '' ? null : comment,
        photoPath: prev[itemId]?.photoPath ?? null,
      },
    }))
  }

  const setItemPhotoLocal = (itemId: string, photoPath: string | null) => {
    setItems((prev) => ({
      ...prev,
      [itemId]: { status: prev[itemId]?.status ?? '', comment: prev[itemId]?.comment ?? null, photoPath },
    }))
  }

  const toggleSection = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const saveButton = (
    <Button variant="filled" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
      {saveMutation.isPending ? t('common.saving') : t('common.save')}
    </Button>
  )

  if (isLoading || !jobcard) {
    return (
      <div className="space-y-6">
        <PageHeader title={t('inspection.vehicleInspection')} back={{ to: `/jobcards/${jobcardId}`, label: t('inspection.jobcard') }} />
        <Skeleton lines={6} />
      </div>
    )
  }

  return (
    <div className="space-y-6 pb-24">
      <PageHeader
        title={t('inspection.vehicleInspection')}
        subtitle={`${carLabel(jobcard.carSnapshot)} · ${jobcard.number}`}
        back={{ to: `/jobcards/${jobcardId}`, label: t('inspection.jobcard') }}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outlined"
              icon={<PrintIcon />}
              onClick={() => window.open(`/jobcards/${jobcardId}/inspection/print`, '_blank')}
            >
              {t('common.print')}
            </Button>
            {saveButton}
          </div>
        }
      />

      <div className="bg-surface-container border border-outline-variant rounded-lg p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label={t('inspection.inspectedByOptional')} value={performedBy} onChange={setPerformedBy} placeholder={t('inspection.inspectedByPlaceholder')} />
          <TextField
            label={t('inspection.dateOptional')}
            value={performedAt}
            onChange={setPerformedAt}
            placeholder={t('inspection.datePlaceholder')}
          />
        </div>
        <TextField
          label={t('inspection.overallNotesOptional')}
          value={overallNotes}
          onChange={setOverallNotes}
          multiline
          rows={2}
          placeholder={t('inspection.overallNotesPlaceholder')}
        />
      </div>

      <div className="bg-surface-container border border-outline-variant rounded-lg p-4 space-y-3">
        <h2 className="text-title-md text-on-surface">{t('inspection.vehiclePhotos')}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {INSPECTION_OVERVIEW_ANGLES.map((angle) => (
            <OverviewPhotoTile
              key={angle.key}
              jobcardId={jobcardId}
              angle={angle.key}
              label={overviewAngleLabel(angle.key, angle.label)}
              photoPath={overviewPhotos[angle.key] ?? null}
              version={photoVersions[`overview-${angle.key}`] ?? 0}
              onUploaded={(path) => {
                setOverviewPhotos((prev) => ({ ...prev, [angle.key]: path }))
                setPhotoVersions((prev) => ({ ...prev, [`overview-${angle.key}`]: Date.now() }))
              }}
              onRemoved={() => {
                setOverviewPhotos((prev) => ({ ...prev, [angle.key]: null }))
                setPhotoVersions((prev) => ({ ...prev, [`overview-${angle.key}`]: Date.now() }))
              }}
            />
          ))}
        </div>
      </div>

      {sections.length === 0 ? (
        <EmptyState
          title={t('inspection.noChecklistYet')}
          description={t('inspection.noChecklistDescription')}
        />
      ) : (
        <div className="space-y-3">
          {sections.map((section) => {
            const isOpen = expanded.has(section.id)
            let checked = 0
            let attention = 0
            for (const item of section.items) {
              const state = getItem(item.id)
              if (state.status !== '') checked++
              const option = state.status
                ? inspectionScaleOption(inspectionTemplateItemScale(item), state.status)
                : undefined
              if (option?.tone === 'warning' || option?.tone === 'error') attention++
            }

            return (
              <div key={section.id} className="bg-surface-container border border-outline-variant rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between gap-3 p-4 text-start interactive hover:bg-surface-high"
                  aria-expanded={isOpen}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {isOpen ? (
                      <ChevronDownIcon className="w-5 h-5 flex-shrink-0 text-on-surface-variant" />
                    ) : (
                      <ChevronRightIcon className="w-5 h-5 flex-shrink-0 text-on-surface-variant" />
                    )}
                    <span className="text-title-md text-on-surface truncate">{section.label}</span>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {attention > 0 && <StatusDot tone="warning" label={t('inspection.flagged', { count: attention })} />}
                    <span className="text-body-sm text-on-surface-variant">
                      {t('inspection.checkedCount', { checked, total: section.items.length })}
                    </span>
                  </div>
                </button>

                {isOpen && (
                  <div className="p-4 pt-0 space-y-4 border-t border-outline-variant">
                    <TextField
                      label={t('inspection.sectionNoteOptional')}
                      value={sectionNotes[section.id] ?? ''}
                      onChange={(value) => setSectionNotes((prev) => ({ ...prev, [section.id]: value }))}
                      placeholder={t('inspection.sectionNotePlaceholder', { section: section.label.toLowerCase() })}
                    />

                    {section.items.length === 0 ? (
                      <p className="text-body-sm text-on-surface-variant">{t('inspection.noItemsInSection')}</p>
                    ) : (
                      <div className="space-y-3">
                        {section.items.map((item) => {
                          const state = getItem(item.id)
                          const scaleKey = inspectionTemplateItemScale(item)
                          const options = INSPECTION_SCALES[scaleKey].map((o) => ({
                            value: o.value,
                            label: scaleOptionLabel(scaleKey, o.value, o.label),
                          }))
                          const currentOption = state.status ? inspectionScaleOption(scaleKey, state.status) : undefined

                          return (
                            <div
                              key={item.id}
                              className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 py-2 border-b border-outline-variant last:border-b-0"
                            >
                              <div className="sm:w-52 flex-shrink-0 flex items-center gap-2">
                                <StatusDot tone={currentOption?.tone ?? 'neutral'} label="" muted={false} />
                                <span className="text-body-md text-on-surface -ms-1">{item.label}</span>
                              </div>
                              <Segmented
                                label={t('inspection.conditionFor', { item: item.label })}
                                options={options}
                                value={state.status}
                                onChange={(status) => setItemStatus(item.id, status)}
                              />
                              <TextField
                                bare
                                label={t('inspection.commentFor', { item: item.label })}
                                value={state.comment ?? ''}
                                onChange={(value) => setItemComment(item.id, value)}
                                placeholder={t('inspection.addCommentOptional')}
                                className="flex-1"
                              />
                              <ItemPhotoControl
                                jobcardId={jobcardId}
                                itemId={item.id}
                                itemLabel={item.label}
                                photoPath={state.photoPath}
                                version={photoVersions[item.id] ?? 0}
                                onUploaded={(path) => {
                                  setItemPhotoLocal(item.id, path)
                                  setPhotoVersions((prev) => ({ ...prev, [item.id]: Date.now() }))
                                }}
                                onRemoved={() => {
                                  setItemPhotoLocal(item.id, null)
                                  setPhotoVersions((prev) => ({ ...prev, [item.id]: Date.now() }))
                                }}
                              />
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      <div className="flex justify-end">{saveButton}</div>
    </div>
  )
}

/** One checklist item's evidence photo: a small thumbnail with an upload/remove control. */
function ItemPhotoControl({
  jobcardId,
  itemId,
  itemLabel,
  photoPath,
  version,
  onUploaded,
  onRemoved,
}: {
  jobcardId: string
  itemId: string
  itemLabel: string
  photoPath: string | null
  version: number
  onUploaded: (photoPath: string) => void
  onRemoved: () => void
}) {
  const snackbar = useSnackbar()
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)

  const handleFile = async (file: File) => {
    setBusy(true)
    try {
      const updated = await api.inspections.uploadItemPhoto(jobcardId, itemId, file)
      const path = updated.items[itemId]?.photoPath
      if (path) onUploaded(path)
    } catch (error) {
      snackbar.error(error instanceof ApiError ? error.message : t('inspection.failedToUploadPhoto'))
    } finally {
      setBusy(false)
    }
  }

  const handleRemove = async () => {
    setBusy(true)
    try {
      await api.inspections.removeItemPhoto(jobcardId, itemId)
      onRemoved()
    } catch (error) {
      snackbar.error(error instanceof ApiError ? error.message : t('inspection.failedToRemovePhoto'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex-shrink-0">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          if (file) void handleFile(file)
        }}
      />
      {photoPath ? (
        <div className="relative w-9 h-9">
          <img
            src={api.inspections.itemPhotoUrl(jobcardId, itemId, 128, version)}
            alt={t('inspection.photoAlt', { item: itemLabel })}
            className="w-9 h-9 rounded object-cover border border-outline-variant"
            loading="lazy"
            decoding="async"
          />
          <button
            type="button"
            onClick={handleRemove}
            disabled={busy}
            aria-label={t('inspection.removePhotoFor', { item: itemLabel })}
            className="absolute -top-1.5 -end-1.5 w-4 h-4 rounded-full bg-error text-on-error flex items-center justify-center text-[10px] leading-none interactive"
          >
            ×
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          aria-label={t('inspection.addPhotoFor', { item: itemLabel })}
          className="w-9 h-9 rounded border border-dashed border-outline-variant text-on-surface-variant interactive hover:border-outline hover:text-on-surface flex items-center justify-center"
        >
          <AddIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  )
}

/** One of the four whole-vehicle overview shots (front/left/right/rear). */
function OverviewPhotoTile({
  jobcardId,
  angle,
  label,
  photoPath,
  version,
  onUploaded,
  onRemoved,
}: {
  jobcardId: string
  angle: InspectionOverviewAngle
  label: string
  photoPath: string | null
  version: number
  onUploaded: (photoPath: string) => void
  onRemoved: () => void
}) {
  const snackbar = useSnackbar()
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)

  const handleFile = async (file: File) => {
    setBusy(true)
    try {
      const updated = await api.inspections.uploadOverviewPhoto(jobcardId, angle, file)
      const path = updated.overviewPhotos[angle]
      if (path) onUploaded(path)
    } catch (error) {
      snackbar.error(error instanceof ApiError ? error.message : t('inspection.failedToUploadPhoto'))
    } finally {
      setBusy(false)
    }
  }

  const handleRemove = async () => {
    setBusy(true)
    try {
      await api.inspections.removeOverviewPhoto(jobcardId, angle)
      onRemoved()
    } catch (error) {
      snackbar.error(error instanceof ApiError ? error.message : t('inspection.failedToRemovePhoto'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col items-center gap-1.5">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          if (file) void handleFile(file)
        }}
      />
      {photoPath ? (
        <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden border border-outline-variant bg-surface-high">
          <img
            src={api.inspections.overviewPhotoUrl(jobcardId, angle, 320, version)}
            alt={label}
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
          <button
            type="button"
            onClick={handleRemove}
            disabled={busy}
            aria-label={t('inspection.removePhotoFor', { item: label })}
            className="absolute top-1 end-1 w-6 h-6 rounded-full bg-error text-on-error flex items-center justify-center text-body-sm interactive"
          >
            ×
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="w-full aspect-[4/3] rounded-lg border border-dashed border-outline-variant flex flex-col items-center justify-center gap-1 text-on-surface-variant interactive hover:border-outline hover:text-on-surface"
        >
          <AddIcon className="w-5 h-5" />
          <span className="text-body-sm">{t('inspection.addPhoto')}</span>
        </button>
      )}
      <span className="text-body-sm text-on-surface-variant">{label}</span>
    </div>
  )
}
